import { UserPermission } from '@/constants/UserPermission';
import type { IUserInfoResponse } from '@/api-v2/web/user';
import type { ComputedRef } from 'vue';
import { computed } from 'vue';
import { UserModule } from '@/store/modules/user';
import { getEffectiveUserRoles } from '@/utils/adminRoles';

/**
 * If possible, prefer {@link useUserQuery} for getting the user object instead.
 */
export default function (): {
  user: ComputedRef<IUserInfoResponse | null>;
  isUserAdmin: ComputedRef<boolean>;
  isUserSuperAdmin: ComputedRef<boolean>;
  isUserFunctionalRightsAdmin: ComputedRef<boolean>;
  isUserConstituentRightsAdmin: ComputedRef<boolean>;
  isUserTrackAccessRightAdmin: ComputedRef<boolean>;
  isUserCmsAdmin: ComputedRef<boolean>;
  hasAnyAdminRole: ComputedRef<boolean>;
  isUserImpersonating: ComputedRef<boolean>;
  isShowUserImpersonatingBanner: ComputedRef<boolean>;
} {
  const user = computed((): IUserInfoResponse | null => UserModule.user);

  const isUserAdmin = computed((): boolean => {
    if (!user.value || !user.value.permission) return false;

    return user.value.permission.includes(UserPermission.ADMIN);
  });

  const isUserSuperAdmin = computed((): boolean => {
    if (!user.value || !user.value.permission) return false;

    return user.value.permission.includes(UserPermission.SUPER_ADMIN);
  });

  const isUserFunctionalRightsAdmin = computed((): boolean => {
    if (!user.value || !user.value.permission) return false;

    return user.value.permission.includes(UserPermission.FUNCTIONAL_RIGHTS_ADMIN);
  });

  const isUserConstituentRightsAdmin = computed((): boolean => {
    if (!user.value || !user.value.permission) return false;

    return user.value.permission.includes(UserPermission.CONSTITUENT_RIGHTS_ADMIN);
  });

  const isUserTrackAccessRightAdmin = computed((): boolean => {
    if (!user.value || !user.value.permission) return false;

    return user.value.permission.includes(UserPermission.TRACK_ACCESS_RIGHT_ADMIN);
  });

  const isUserCmsAdmin = computed((): boolean => {
    if (!user.value || !user.value.permission) return false;

    return user.value.permission.includes(UserPermission.CMS_ADMIN);
  });

  const hasAnyAdminRole = computed((): boolean => getEffectiveUserRoles(user.value).length > 0);

  const isUserImpersonating = computed((): boolean => {
    if (!user.value || !user.value.permission) return false;

    return user.value.permission.includes(UserPermission.IMPERSONATE);
  });

  const isShowUserImpersonatingBanner = computed((): boolean => {
    if (!user.value || !user.value.permission) return false;

    return user.value.permission.includes(UserPermission.WARN_IMPERSONATE);
  });

  return {
    user,
    isUserAdmin,
    isUserSuperAdmin,
    isUserFunctionalRightsAdmin,
    isUserConstituentRightsAdmin,
    isUserTrackAccessRightAdmin,
    isUserCmsAdmin,
    hasAnyAdminRole,
    isUserImpersonating,
    isShowUserImpersonatingBanner,
  };
}
